import React, { useState, useEffect, useCallback } from 'react';
import { Form, Select, Input, Button, Icon, message } from 'antd';
import { VersionTagPopup } from './styles';
import { createVersionTag } from '../../../utils/services/complexVersionsAPI';
import { CONTEXT_KEYS, useArtboardContext } from '../../../context/complexArtboardContext';
import {
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
  useDocumentContext,
} from '../../../context/complexDocumentContext';
import { VERSION_CONTEXT_KEYS, useVersionContext } from '../../../context/complexVersionContext';
import { withTranslation } from 'react-i18next';
import { ADD_TAG_FORM } from './translationValues';
import { COMPLEX_DOCUMENT_VERSION, CHANNELS } from '../../../utils/complexDocumentConstants';

const NAMESPACE = ['ADD_TAG_FORM'];

const { Option } = Select;

const AddTagForm = props => {
  const { t, createTagFormVisible, onSuccess } = props;
  const [isCreatingTag, setIsCreatingTag] = useState(false);

  useEffect(() => {
    if (!createTagFormVisible) {
      props.form.resetFields();
    }
  }, [createTagFormVisible]);

  const currentDesignSelector = useCallback(store => store[CONTEXT_KEYS.CURRENT_DESIGN], []);
  const [currentDesign] = useArtboardContext(currentDesignSelector, `AddTagForm`);

  const documentIdSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID], []);
  const [documentId] = useDocumentContext(documentIdSelector, `AddTagForm`);

  const isEmailSelector = useCallback(
    store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_EMAIL_TEMPLATE],
    [],
  );
  const [isEmailTemplate] = useDocumentContext(isEmailSelector, `AddTagForm`);

  const versionsSelector = useCallback(store => store[VERSION_CONTEXT_KEYS.TAGS_LIST], []);
  const [tags, updateVersionStore] = useVersionContext(versionsSelector, `AddTagForm`);

  const currentVersionSelector = useCallback(
    store => store[VERSION_CONTEXT_KEYS.CURRENT_VISIBLE_VERSION],
    [],
  );
  const [currentVersion] = useVersionContext(currentVersionSelector, `AddTagForm`);

  const lifecycleSelector = useCallback(
    store => store[VERSION_CONTEXT_KEYS.LIFECYCLE_TIMELINE],
    [],
  );
  const [lifeCycle] = useVersionContext(lifecycleSelector, `AddTagForm`);

  const { getFieldDecorator, getFieldsError } = props.form;

  const hasErrors = fieldsError => {
    return Object.keys(fieldsError).some(field => fieldsError[field]);
  };

  const handleSubmit = e => {
    e.preventDefault();
    props.form.validateFields(async (err, values) => {
      if (!err) {
        try {
          setIsCreatingTag(true);
          const payload = {
            ...values,
            tagName: values.tagName.trim(),
            channel: isEmailTemplate ? CHANNELS.EMAIL : CHANNELS.PRINT,
            designType: currentDesign,
            designId: documentId,
          };
          const response = await createVersionTag(payload);
          if (response?.statusCode === 5700) {
            updateVersionStore({
              [VERSION_CONTEXT_KEYS.TAGS_LIST]: [response?.data, ...tags],
            });
            onSuccess();
          } else if (
            response?.statusCode === 5701 &&
            response?.errors?.some(err => err.code === 5700)
          ) {
            message.error(
              t(
                ADD_TAG_FORM.TAG_EXISTS_ERROR_MESSAGE.key,
                ADD_TAG_FORM.TAG_EXISTS_ERROR_MESSAGE.value,
              ),
            );
          } else {
            message.error(
              t(
                ADD_TAG_FORM.TAG_CREATION_FAILED_ERROR.key,
                ADD_TAG_FORM.TAG_CREATION_FAILED_ERROR.value,
              ),
            );
          }
        } catch (e) {
          message.error(
            t(
              ADD_TAG_FORM.TAG_CREATION_FAILED_ERROR.key,
              ADD_TAG_FORM.TAG_CREATION_FAILED_ERROR.value,
            ),
          );
          console.error(e);
        } finally {
          setIsCreatingTag(false);
        }
      }
    });
  };

  const versionMap = {
    qaready: COMPLEX_DOCUMENT_VERSION.SANDBOX,
    published: COMPLEX_DOCUMENT_VERSION.LIVE,
  };

  const defaultVersion = versionMap[currentVersion] || COMPLEX_DOCUMENT_VERSION.DRAFT;

  return (
    <VersionTagPopup>
      <h1>{t(ADD_TAG_FORM.CREATE_NEW_TAG_TITLE.key, ADD_TAG_FORM.CREATE_NEW_TAG_TITLE.value)}</h1>
      <Form onSubmit={handleSubmit}>
        <div className="tag-info">
          <Form.Item>
            <label>{t(ADD_TAG_FORM.VERSION_LABEL.key, ADD_TAG_FORM.VERSION_LABEL.value)}</label>
            {getFieldDecorator('env', { initialValue: defaultVersion })(
              <Select defaultValue={currentVersion}>
                <Option value={COMPLEX_DOCUMENT_VERSION.DRAFT}>
                  {t(ADD_TAG_FORM.VERSION_DRAFT.key, ADD_TAG_FORM.VERSION_DRAFT.value)}
                </Option>
                {lifeCycle[COMPLEX_DOCUMENT_VERSION.QA_READY] && (
                  <Option value={COMPLEX_DOCUMENT_VERSION.SANDBOX}>
                    {t(ADD_TAG_FORM.VERSION_TESTING.key, ADD_TAG_FORM.VERSION_TESTING.value)}
                  </Option>
                )}
                {lifeCycle[COMPLEX_DOCUMENT_VERSION.PUBLISHED] && (
                  <Option value={COMPLEX_DOCUMENT_VERSION.LIVE}>
                    {t(ADD_TAG_FORM.VERSION_PUBLISHED.key, ADD_TAG_FORM.VERSION_PUBLISHED.value)}
                  </Option>
                )}
              </Select>,
            )}
          </Form.Item>

          <Form.Item>
            <label>{t(ADD_TAG_FORM.TAG_NAME_LABEL.key, ADD_TAG_FORM.TAG_NAME_LABEL.value)}</label>
            {getFieldDecorator('tagName', {
              rules: [
                {
                  required: true,
                  whitespace: true,
                  message: t(
                    ADD_TAG_FORM.TAG_NAME_EMPTY_ERROR.key,
                    ADD_TAG_FORM.TAG_NAME_EMPTY_ERROR.value,
                  ),
                },
              ],
            })(
              <Input
                placeholder={t(
                  ADD_TAG_FORM.TAG_NAME_PLACEHOLDER.key,
                  ADD_TAG_FORM.TAG_NAME_PLACEHOLDER.value,
                )}
                autoFocus
              />,
            )}
          </Form.Item>
        </div>

        <Button
          type="primary"
          htmlType="submit"
          disabled={isCreatingTag || hasErrors(getFieldsError())}
        >
          {isCreatingTag && <Icon type="loading" />}{' '}
          {t(ADD_TAG_FORM.CREATE_BUTTON.key, ADD_TAG_FORM.CREATE_BUTTON.value)}
        </Button>
      </Form>
    </VersionTagPopup>
  );
};

const CreateTagForm = Form.create({ name: 'create_tag_form' })(AddTagForm);
export default withTranslation(NAMESPACE)(CreateTagForm);
